import { useEffect, useId, useState } from 'react';

import { Badge, Button, Divider, Icon } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Input, Textarea } from '@ds/forms';
import { centavosDoTexto, textoDosCentavos } from '@/comuns/dinheiro';
import { dataSimples, moeda } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import { SITUACAO_DA_NOTA, documentoFormatado } from '@/data/notasFiscais';
import type { NotaFiscal } from '@/data/notasFiscais';

export interface NotaFiscalModalProps {
    nota: NotaFiscal;
    /** A empresa está ligada ao emissor? Sem isso, "Emitir" fica desligado com o porquê. */
    emissorLigado: boolean;
    /** Quem vê a nota da plataforma do lado da clínica só lê. */
    somenteLeitura?: boolean;
    onFechar: () => void;
    onMudou: () => void;
}

function Linha({ rotulo, valor }: { rotulo: string; valor: string | undefined }) {
    return (
        <>
            <dt style={{ color: 'var(--text-secondary)' }}>{rotulo}</dt>
            <dd style={{ margin: 0, textAlign: 'right', fontWeight: 500, color: valor ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>{valor || '—'}</dd>
        </>
    );
}

const LINK = { color: 'var(--text-brand)', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 } as const;

/**
 * A nota como vai à prefeitura. No rascunho — e na rejeitada, que a prefeitura
 * mandou corrigir — dá para trocar descrição e valor, e reler tomador e serviço
 * dos cadastros: o rascunho congela o que havia no dia em que foi preparado, e
 * corrigir o CPF no paciente não muda a nota sozinho. Autorizada, a nota mostra
 * PDF, XML e o espelho na prefeitura, e se cancela com motivo.
 */
export function NotaFiscalModal({ nota, emissorLigado, somenteLeitura = false, onFechar, onMudou }: NotaFiscalModalProps) {
    const aviso = useAviso();
    const idDescricao = useId();
    const idValor = useId();
    const [descricao, setDescricao] = useState(nota.descricao);
    const [valor, setValor] = useState(textoDosCentavos(nota.valorServicosCentavos));
    const [ocupado, setOcupado] = useState(false);
    const [erro, setErro] = useState<string | null>(null);
    const [descartando, setDescartando] = useState(false);
    const [cancelando, setCancelando] = useState(false);
    const [motivo, setMotivo] = useState('');
    const idMotivo = useId();

    useEffect(() => {
        setDescricao(nota.descricao);
        setValor(textoDosCentavos(nota.valorServicosCentavos));
    }, [nota]);

    /* A rejeitada volta a ser editável: corrige e emite de novo, com o mesmo número. */
    const rascunho = (nota.situacao === 'rascunho' || nota.situacao === 'rejeitada') && !somenteLeitura;
    const aguardando = nota.situacao === 'na_fila' || nota.situacao === 'processando' || nota.situacao === 'cancelando';
    const valorCentavos = centavosDoTexto(valor);
    const mudouTexto = descricao.trim() !== nota.descricao || valorCentavos !== nota.valorServicosCentavos;
    const situacao = SITUACAO_DA_NOTA[nota.situacao];

    const agir = async (acao: () => Promise<void>, sucesso: string) => {
        setOcupado(true);
        setErro(null);
        try {
            await acao();
            aviso({ title: sucesso });
            onMudou();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível concluir.');
        } finally {
            setOcupado(false);
        }
    };

    const atualizar = () => agir(async () => {
        await dados().atualizarRascunhoDaNota(nota.id, {
            ...(descricao.trim() !== nota.descricao ? { descricao } : {}),
            ...(valorCentavos !== null && valorCentavos !== nota.valorServicosCentavos ? { valorCentavos } : {}),
        });
    }, 'Rascunho atualizado');

    const descartar = () => agir(async () => {
        await dados().descartarRascunhoDaNota(nota.id);
        onFechar();
    }, 'Rascunho descartado');

    const emitir = () => agir(() => dados().solicitarEmissaoDaNota(nota.id), 'Nota enviada ao emissor');

    const cancelar = () => agir(async () => {
        await dados().cancelarNotaFiscal(nota.id, motivo);
        setCancelando(false);
        setMotivo('');
    }, 'Cancelamento pedido');

    const podeEmitir = rascunho && nota.pendencias.length === 0 && emissorLigado && !mudouTexto;

    return (
        <Modal
            open width={620} onClose={onFechar}
            title={nota.origem === 'plataforma' ? `Nota da mensalidade · ${nota.clinicaNome ?? 'clínica'}` : `Nota fiscal · ${nota.tomadorNome ?? 'paciente'}`}
            description={nota.tituloDescricao ?? (nota.faturaCompetencia ? `Fatura ${nota.faturaCompetencia}` : undefined)}
            footer={(
                <>
                    {rascunho && !descartando && <Button variant="ghost" onClick={() => setDescartando(true)}>Descartar</Button>}
                    {nota.situacao === 'autorizada' && !somenteLeitura && !cancelando && (
                        <Button variant="ghost" onClick={() => setCancelando(true)}>Cancelar nota</Button>
                    )}
                    <span style={{ marginLeft: 'auto' }} />
                    <Button variant="secondary" onClick={onFechar}>Fechar</Button>
                    {rascunho && (
                        <Button variant="secondary" iconLeft={<Icon name="refresh-cw" size={15} />} onClick={() => { void atualizar(); }} disabled={ocupado || valorCentavos === null}>
                            {mudouTexto ? 'Salvar e reler cadastros' : 'Reler cadastros'}
                        </Button>
                    )}
                    {rascunho && (
                        <Button onClick={() => { void emitir(); }} disabled={!podeEmitir || ocupado} loading={ocupado && podeEmitir}>
                            {nota.situacao === 'rejeitada' ? 'Emitir de novo' : 'Emitir nota'}
                        </Button>
                    )}
                </>
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}
                {descartando && (
                    <Alert
                        tone="danger" title="Descartar este rascunho?"
                        action={(
                            <span style={{ display: 'flex', gap: 8 }}>
                                <Button size="sm" variant="ghost" onClick={() => setDescartando(false)}>Manter</Button>
                                <Button size="sm" variant="danger" onClick={() => { void descartar(); }} disabled={ocupado}>Descartar</Button>
                            </span>
                        )}
                    >
                        Nenhuma nota foi autorizada: o rascunho some e o título volta a poder ter nota.
                    </Alert>
                )}
                {cancelando && (
                    <Alert tone="danger" title="Cancelar esta nota na prefeitura?">
                        <FormField label="Motivo" htmlFor={idMotivo} hint="De 15 a 255 caracteres. Vai para a prefeitura." style={{ marginTop: 6 }}>
                            <Textarea id={idMotivo} rows={2} value={motivo} onChange={setMotivo} />
                        </FormField>
                        <span style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                            <Button size="sm" variant="ghost" onClick={() => { setCancelando(false); setMotivo(''); }}>Manter a nota</Button>
                            <Button size="sm" variant="danger" onClick={() => { void cancelar(); }} disabled={ocupado || motivo.trim().length < 15 || motivo.trim().length > 255}>
                                Cancelar nota
                            </Button>
                        </span>
                    </Alert>
                )}

                <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', padding: '14px 16px', background: 'var(--surface-secondary)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)' }}>
                    <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Valor do serviço</span>
                    <span style={{ marginLeft: 'auto', fontSize: 22, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{moeda(nota.valorServicosCentavos)}</span>
                    <Badge tone={situacao.tom} dot>{situacao.rotulo}</Badge>
                </div>

                {rascunho && nota.pendencias.length > 0 && (
                    <Alert tone="warning" title="Falta para emitir" dismissible={false}>
                        <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                            {nota.pendencias.map((p) => <li key={p}>{p}</li>)}
                        </ul>
                        <span style={{ display: 'block', marginTop: 6 }}>
                            Dados do paciente se corrigem na ficha dele; os da empresa, em Dados fiscais. Depois, "Reler cadastros".
                        </span>
                    </Alert>
                )}
                {rascunho && nota.pendencias.length === 0 && !emissorLigado && (
                    <Alert tone="info" title="Pronta, falta ligar o emissor" dismissible={false}>
                        Os dados estão completos. Em Dados fiscais, use "Ligar ao emissor" — até lá, nada é enviado.
                    </Alert>
                )}
                {aguardando && (
                    <Alert tone="info" title={nota.situacao === 'cancelando' ? 'Cancelamento enviado' : 'Enviada ao emissor'} dismissible={false}>
                        A resposta da prefeitura chega sozinha, em geral em poucos minutos. Esta tela se atualiza.
                    </Alert>
                )}
                {nota.mensagem && (
                    <Alert
                        tone={nota.situacao === 'rejeitada' ? 'danger' : nota.situacao === 'autorizada' ? 'warning' : 'neutral'}
                        title={nota.situacao === 'rejeitada' ? 'A prefeitura recusou' : 'Resposta do emissor'} dismissible={false}
                    >
                        {nota.mensagem}
                        {nota.situacao === 'rejeitada' && (
                            <span style={{ display: 'block', marginTop: 6 }}>Corrija o cadastro, use "Reler cadastros" e emita de novo.</span>
                        )}
                    </Alert>
                )}
                {(nota.pdfUrl || nota.xmlUrl || nota.espelhoUrl) && (
                    <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 13 }}>
                        {nota.pdfUrl && <a href={nota.pdfUrl} target="_blank" rel="noopener noreferrer" style={LINK}><Icon name="file-text" size={15} />PDF da nota</a>}
                        {nota.xmlUrl && <a href={nota.xmlUrl} target="_blank" rel="noopener noreferrer" style={LINK}><Icon name="file-code" size={15} />XML</a>}
                        {nota.espelhoUrl && <a href={nota.espelhoUrl} target="_blank" rel="noopener noreferrer" style={LINK}><Icon name="external-link" size={15} />Ver na prefeitura</a>}
                    </div>
                )}

                {rascunho ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: 14 }}>
                        <FormField label="Descrição do serviço" htmlFor={idDescricao} style={{ gridColumn: '1 / -1' }}>
                            <Textarea id={idDescricao} rows={3} value={descricao} onChange={setDescricao} />
                        </FormField>
                        <FormField label="Valor" htmlFor={idValor} error={valor.trim() && valorCentavos === null ? 'Valor inválido' : undefined}>
                            <Input id={idValor} value={valor} onChange={setValor} inputMode="decimal" iconLeft={<span style={{ fontSize: 13 }}>R$</span>} />
                        </FormField>
                    </div>
                ) : (
                    <p style={{ margin: 0, fontSize: 13.5, lineHeight: '20px', whiteSpace: 'pre-wrap' }}>{nota.descricao}</p>
                )}

                <Divider label="Tomador" spacing={0} />
                <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 20px', fontSize: 13 }}>
                    <Linha rotulo="Nome" valor={nota.tomadorNome} />
                    <Linha rotulo={(nota.tomadorDocumento ?? '').length === 14 ? 'CNPJ' : 'CPF'} valor={nota.tomadorDocumento ? documentoFormatado(nota.tomadorDocumento) : undefined} />
                    <Linha rotulo="E-mail" valor={nota.tomadorEmail} />
                    <Linha rotulo="Endereço" valor={nota.tomadorEndereco} />
                </dl>

                <Divider label="Serviço e imposto" spacing={0} />
                <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 20px', fontSize: 13 }}>
                    <Linha rotulo="Item da LC 116" valor={nota.itemListaServico} />
                    <Linha rotulo="Código do município" valor={nota.codigoTributacaoMunicipio} />
                    {nota.codigoTributacaoNacional && <Linha rotulo="Código nacional" valor={nota.codigoTributacaoNacional} />}
                    <Linha rotulo="Alíquota de ISS" valor={nota.aliquotaIss !== undefined ? `${String(nota.aliquotaIss).replace('.', ',')}%` : undefined} />
                    <Linha rotulo="ISS" valor={`${moeda(nota.valorIssCentavos)}${nota.issRetido ? ' · retido pelo tomador' : ''}`} />
                    <Linha rotulo="Competência" valor={nota.competencia ? dataSimples(nota.competencia).slice(3) : undefined} />
                    {nota.rpsNumero !== undefined && <Linha rotulo="RPS" valor={`${nota.rpsSerie ?? ''}-${nota.rpsNumero}`} />}
                    {nota.numero && <Linha rotulo="Número da nota" valor={nota.numero} />}
                    {nota.codigoVerificacao && <Linha rotulo="Código de verificação" valor={nota.codigoVerificacao} />}
                    {nota.autorizadaEm && <Linha rotulo="Autorizada em" valor={dataSimples(nota.autorizadaEm.slice(0, 10))} />}
                    {nota.situacao === 'cancelada' && nota.cancelamentoMotivo && <Linha rotulo="Motivo do cancelamento" valor={nota.cancelamentoMotivo} />}
                </dl>
            </div>
        </Modal>
    );
}
